import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import {
  Bell,
  Search,
  Menu,
  LogOut,
  AlertCircle,
  Shield,
  Key,
  UserCheck,
  Clock,
  Info,
  CheckCircle
} from 'lucide-react';
import { useAuth } from '@/auth/authContext';
import { ThemeToggle } from '@/components/theme/ThemeToggle';
import { useActiveNotifications, useUnreadCount, useMarkAsRead } from '@/api-services/hooks/useNotifications';
import type { ManageNotificationDTO } from '@/api-services/hooks/notificationTypes';
import { Loader2 } from 'lucide-react';
import AIAgent from '../aiagent/AIAgent';

// Helper function to get user type from role
const getUserTypeFromRole = (role?: string): string => {
  if (!role) return 'ADMIN';
  const roleUpper = role.toUpperCase();
  if (roleUpper.includes('ADMIN') || roleUpper === 'SUPER_ADMIN') return 'ADMIN';
  if (roleUpper.includes('DRIVER')) return 'DRIVER';
  if (roleUpper.includes('PASSENGER') || roleUpper.includes('RIDER')) return 'RIDER';
  return 'ADMIN';
};

// Helper function to format time since notification
const formatTimeSince = (dateString?: string): string => {
  if (!dateString) return 'Just now';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

// Helper function to get notification icon and color
const getNotificationIcon = (notification: ManageNotificationDTO) => {
  const priority = notification.priority?.toUpperCase() || 'INFO';
  const messageType = notification.messageType?.toUpperCase() || '';

  if (messageType.includes('ALERT') || priority === 'HIGH') {
    return { icon: <AlertCircle className="w-4 h-4" />, color: 'text-destructive' };
  }
  if (messageType.includes('WARNING') || priority === 'MEDIUM') {
    return { icon: <AlertCircle className="w-4 h-4" />, color: 'text-warning' };
  }
  if (messageType.includes('SUCCESS')) {
    return { icon: <CheckCircle className="w-4 h-4" />, color: 'text-success' };
  }
  return { icon: <Info className="w-4 h-4" />, color: 'text-info' };
};

const EnhancedHeader = ({ setSidebarOpen }) => {
  const navigate = useNavigate();
  const { user, logout, hasPermission } = useAuth();
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  // Get user ID and type for notifications
  const userId = user?.id ? parseInt(user.id) : undefined;
  const userType = getUserTypeFromRole(user?.role);

  // Fetch recent notifications for header dropdown
  const { data: activeNotifications, loading: notificationsLoading, refetch: refetchNotifications } = useActiveNotifications({
    page: 0,
    size: 10,
    enabled: !!userId,
  });

  // Filter for unread notifications from active notifications
  const unreadNotifications = activeNotifications?.filter(n => !n.isRead) || [];

  const { data: unreadCount, refetch: refetchCount } = useUnreadCount(
    userId,
    userType,
    {
      enabled: !!userId,
    }
  );

  const markAsRead = useMarkAsRead({
    onSuccess: () => {
      refetchNotifications();
      refetchCount();
    },
  });

  // Handle notification click - mark as read and go to notifications page
  const handleNotificationClick = async (notification: ManageNotificationDTO) => {
    if (notification.id) {
      await markAsRead(notification.id);
    }
    setNotificationsOpen(false);
    navigate('/admin/notifications');
  };

  // Refetch notifications when dropdown opens
  useEffect(() => {
    if (notificationsOpen && userId) {
      refetchNotifications();
      refetchCount();
    }
  }, [notificationsOpen, userId, refetchNotifications, refetchCount]);

  const getUserInitials = (firstName?: string, lastName?: string) => {
    if (!firstName || !lastName) return 'U';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getRoleDisplayName = (role?: string) => {
    if (!role) return 'User';
    return role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getRoleBadgeColor = (role?: string) => {
    switch (role) {
      case 'super_admin':
        return 'bg-gradient-to-r from-purple-500 to-pink-500 text-white';
      case 'admin':
        return 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white';
      case 'driver':
        return 'bg-gradient-to-r from-green-500 to-emerald-500 text-white';
      case 'passenger':
        return 'bg-gradient-to-r from-orange-500 to-yellow-500 text-white';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    {
      icon: <Key className="w-4 h-4" />,
      label: 'Security Settings',
      action: () => navigate('/admin/security/change-password'),
      show: true
    },
    {
      icon: <UserCheck className="w-4 h-4" />,
      label: 'User Management',
      action: () => navigate('/admin/users/list'),
      show: hasPermission('user', 'read')
    }
  ].filter(item => item.show);

  return (
    <header className="bg-card border-b border-border px-6 py-4">
      <div className="flex items-center justify-between">
        {/* Left side */}
        <div className="flex items-center space-x-4">
          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </Button>

          {/* Mobile Logo */}
          <div
            className="lg:hidden flex items-center cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate('/admin/dashboard')}
          >
            <img
              src="/assets/logo/Aboosto-03.svg"
              alt="Aboosto"
              className="h-8 w-8"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = '/assets/logo/Aboosto-03.png';
              }}
            />
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center space-x-4">
          {/* <AIAgent /> */}
          {/* Theme Toggle */}
          <ThemeToggle />

          {/* Notifications */}
          <DropdownMenu open={notificationsOpen} onOpenChange={setNotificationsOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="relative">
                <Bell className="w-5 h-5" />
                {unreadCount !== undefined && unreadCount > 0 && (
                  <Badge
                    variant="destructive"
                    className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center p-0 text-xs"
                  >
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 max-h-none overflow-hidden">
              <div className="p-2">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-sm">Notifications</h4>
                  {unreadCount !== undefined && unreadCount > 0 && (
                    <span className="text-xs text-muted-foreground">{unreadCount} unread</span>
                  )}
                </div>

                {notificationsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : !unreadNotifications || unreadNotifications.length === 0 ? (
                  <div className="text-center py-8">
                    <Bell className="w-12 h-12 mx-auto text-muted-foreground/50 mb-2" />
                    <p className="text-sm text-muted-foreground">No new notifications</p>
                  </div>
                ) : (
                  <div className="space-y-1 max-h-80 overflow-y-auto">
                    {unreadNotifications.map((notification) => {
                      const { icon, color } = getNotificationIcon(notification);
                      return (
                        <div
                          key={notification.id}
                          onClick={() => handleNotificationClick(notification)}
                          className={`flex items-start space-x-2 p-2 rounded-lg cursor-pointer transition-colors ${
                            notification.priority === 'HIGH'
                              ? 'bg-destructive/10 hover:bg-destructive/20'
                              : 'bg-muted/50 hover:bg-muted'
                          }`}
                        >
                          <div className={`${color} mt-0.5 flex-shrink-0`}>
                            {icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-foreground truncate">
                              {notification.title || 'Notification'}
                            </p>
                            <p className="text-xs text-muted-foreground line-clamp-2">
                              {notification.message}
                            </p>
                            <div className="flex items-center mt-1 text-xs text-muted-foreground">
                              <Clock className="w-3 h-3 mr-1" />
                              {formatTimeSince(notification.createdDate)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="mt-3 pt-2 border-t">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start"
                    onClick={() => navigate('/admin/notifications')}
                  >
                    View all notifications
                  </Button>
                </div>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Profile Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="flex items-center space-x-2 h-auto p-1">
                <div className="relative">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.profilePicture} alt={user?.firstName} />
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                      {getUserInitials(user?.firstName, user?.lastName)}
                    </AvatarFallback>
                  </Avatar>
                  {/* Status indicator */}
                  <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-background ${
                    user?.status === 'active' ? 'bg-green-500' :
                    user?.status === 'inactive' ? 'bg-gray-400' :
                    user?.status === 'suspended' ? 'bg-red-500' :
                    'bg-yellow-500'
                  }`} />
                </div>
                <div className="hidden sm:block text-left">
                  <div className="text-sm font-medium text-foreground">
                    {user?.firstName} {user?.lastName}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge className={`text-xs ${getRoleBadgeColor(user?.role)}`}>
                      {getRoleDisplayName(user?.role)}
                    </Badge>
                  </div>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="flex items-center space-x-2">
                <Shield className="w-4 h-4" />
                <div>
                  <div className="font-medium">Account</div>
                  <div className="text-xs text-muted-foreground">
                    {user?.email}
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />

              {menuItems.map((item, index) => (
                <DropdownMenuItem
                  key={index}
                  onClick={item.action}
                  className="cursor-pointer"
                >
                  {item.icon}
                  <span className="ml-2">{item.label}</span>
                </DropdownMenuItem>
              ))}

              {menuItems.length > 0 && <DropdownMenuSeparator />}

              <DropdownMenuItem
                onClick={handleLogout}
                className="cursor-pointer text-red-600 focus:text-red-600"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default EnhancedHeader;